/**
 * The clause walk's single step (R6; extracted at the 500-line cap in R12;
 * narrowed to one clause in R21) — execute ONE parsed clause against the
 * walk's committed state: a consumed turn on success, a stamped-and-committed
 * refusal with its §8.8 abandonment coda on rejection, and a stop when the
 * clause ends the story terminally (R11). A rejected clause is also an
 * OCCURRENCE (R12): its listeners speak after the refusal.
 *
 * The loop that used to live here (`runClauses`) moved to `walk-clauses.ts`
 * when R21 made it a parse-and-execute interleave — each clause's PARSE now
 * happens between executions, so the walker owns the loop and this module
 * owns what one clause does to a walk.
 *
 * @packageDocumentation
 */

import type { RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { ActionRejection } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { commandInHandOf } from '../conditions/command-in-hand.js';
import { bumpResponseUsage, speakSlot, type SpokenResponse } from '../defaults/default-responses.js';
import { respondToInputOccurrence } from '../narrative/occurrences.js';
import { terminalEndingOf } from '../narrative/session-ended.js';
import { executeTurn, type TurnExecutionResult } from './execute-turn.js';
import { appendTranscript, inputNamespaceCursor, stampNarrativeLines } from './transcript-cursor.js';

/** The walk of an input's clauses, as committed so far. */
export interface ClauseWalk {
	/** This input's transcript entries so far (echo + turn entries + any refusal). */
	readonly entries: readonly RuntimeTranscriptEntry[];
	/** Fired-event metadata aggregated across executed clauses. */
	readonly firedEventIds: TurnExecutionResult['firedEventIds'];
	/** The next free sequence number in this input's id namespace. */
	readonly inputSequence: number;
	/**
	 * The refusal that stopped the walk, when one did (its narratives are
	 * already committed) — PAIRED with the folded text of the clause it
	 * stopped, because the one place a rejection is ever constructed always
	 * knows its clause (F5: a question parks against the CLAUSE, never the
	 * whole input). Pairing the two makes the
	 * rejection-without-a-clause illegal state unrepresentable
	 * (engineering_standards §2.1); it retired three `v8 ignore` spreads and
	 * `withPendingCompletion`'s dead fallback when R21 landed it.
	 */
	readonly rejection?: {
		readonly clauseText: string;
		readonly rejection: ActionRejection;
	};
	/** The committed state — executed turns and any refusal narratives included. */
	readonly state: RuntimeState;
}

/** How one clause's execution left the walk. */
export interface ClauseStep {
	/**
	 * Whether the walk must stop here: a refusal spoke (`walk.rejection` is
	 * present) or the clause ended the story terminally (R11 — the remaining
	 * clauses go unread, exactly as a next input would).
	 */
	readonly stopped: boolean;
	readonly walk: ClauseWalk;
}

/**
 * Open a walk over an input whose history + echo are already committed.
 *
 * @param openedState - The state WITH history + echo already committed
 * @param echoEntries - The player-echo entries (head of this input's entry list)
 * @returns The walk before any clause has run
 */
export const openClauseWalk = (openedState: RuntimeState, echoEntries: readonly RuntimeTranscriptEntry[]): ClauseWalk => ({
	entries: echoEntries,
	firedEventIds: [],
	inputSequence: echoEntries.length,
	state: openedState,
});

/**
 * Execute one clause onto the walk; on refusal, stamp and commit its
 * narratives, let its occurrence listeners speak, and speak the §8.8
 * abandonment coda naming everything left undone.
 *
 * @param bundle - The loaded bundle
 * @param walk - The walk so far (entries and state committed)
 * @param command - The clause to run
 * @param inputOrdinal - This input's ordinal (id namespace)
 * @param abandonedClauseTexts - The clause texts a refusal here would abandon, in input order (§8.8)
 * @param rejectionOverride - §3.2 (D84): when the input is an ANSWER, the caller may re-voice the refusal it earns in the QUESTION's terms — the walk asks; the caller, which alone knows what was asked, decides. `undefined` keeps the rejection's own narratives.
 * @returns The stepped walk, and whether it must stop
 */
export const executeClauseOntoWalk = (
	bundle: EngineGameBundle,
	walk: ClauseWalk,
	command: ParsedCommand,
	inputOrdinal: number,
	abandonedClauseTexts: readonly string[],
	rejectionOverride?: (rejection: ActionRejection) => SpokenResponse | undefined,
): ClauseStep => {
	const turnOutcome = executeTurn(bundle, walk.state, command);

	if (turnOutcome.type === 'action_rejected') {
		const spokenOverride = rejectionOverride?.(turnOutcome.rejection);
		const stamped = stampNarrativeLines(
			bundle,
			walk.state,
			inputNamespaceCursor(inputOrdinal, walk.inputSequence, walk.state),
			spokenOverride === undefined ? turnOutcome.rejection.narratives : [spokenOverride.line],
			'action_rejected',
		);

		// D58: a refusal advances the pools it spoke through, exactly as the
		// parse teaches have since D30 — session metadata, not world state,
		// and no turn consumed. An override advances ITS slot: the count
		// follows what actually spoke.
		const refusedState = appendTranscript(walk.state, stamped.entries);
		// R12: a rejected command is an occurrence — refusal first, then the
		// listeners (she comments AFTER the world says no).
		const responded = respondToInputOccurrence(
			bundle,
			{
				...refusedState,
				session: {
					...refusedState.session,
					responseUsage: bumpResponseUsage(
						refusedState.session.responseUsage,
						spokenOverride === undefined ? turnOutcome.rejection.spokenSlots : [spokenOverride.slot],
					),
				},
			},
			inputOrdinal,
			walk.inputSequence + stamped.entries.length,
			{ commandInHand: commandInHandOf(command), detail: turnOutcome.rejection.reason.type, family: 'command_rejected' },
		);

		const stoppedWalk: ClauseWalk = {
			...walk,
			entries: [...walk.entries, ...stamped.entries, ...responded.entries],
			inputSequence: walk.inputSequence + stamped.entries.length + responded.entries.length,
			rejection: { clauseText: command.clauseText, rejection: turnOutcome.rejection },
			state: responded.state,
		};

		// §8.8: everything after the refused clause is abandoned — the clauses
		// behind it AND any text the parser never reached. Say so.
		return {
			stopped: true,
			walk:
				abandonedClauseTexts.length === 0
					? stoppedWalk
					: speakChainAbandoned(bundle, stoppedWalk, inputOrdinal, abandonedClauseTexts),
		};
	}

	const steppedWalk: ClauseWalk = {
		...walk,
		entries: [...walk.entries, ...turnOutcome.result.transcriptEntries],
		firedEventIds: [...walk.firedEventIds, ...turnOutcome.result.firedEventIds],
		state: turnOutcome.result.nextState,
	};

	// R11: a clause that ends the story TERMINALLY ends the input — the
	// remaining clauses go unread, exactly as a next input would. "go north
	// then take the bar" where north concludes the game owes nobody a bar.
	return { stopped: terminalEndingOf(bundle, steppedWalk.state) !== undefined, walk: steppedWalk };
};

/**
 * Speak the §8.8 abandonment line onto a stopped walk: one line, once, on the
 * world's channel, naming the clauses that will not run. Exported for the
 * arms that stop WITHOUT a refusal (a mid-chain clarification or parse
 * failure abandons its tail the same way).
 *
 * @param bundle - The loaded bundle
 * @param walk - The stopped walk (entries and state committed so far)
 * @param inputOrdinal - This input's id namespace
 * @param abandonedClauseTexts - The clause texts left undone, in input order
 * @returns The walk with the line spoken, committed, and its slot usage bumped
 */
export const speakChainAbandoned = (
	bundle: EngineGameBundle,
	walk: ClauseWalk,
	inputOrdinal: number,
	abandonedClauseTexts: readonly string[],
): ClauseWalk =>
	speakLineOntoWalk(bundle, walk, inputOrdinal, 'chainAbandoned', 'chain_abandoned', {
		abandoned: abandonedClauseTexts.join(', '),
	});

/**
 * Speak one response slot onto a walk — stamped into the input's namespace,
 * committed, usage bumped. The one shape every walk-level speaker shares
 * (the §8.8 coda above; the R10/D81 printed ask), extracted when the second
 * speaker arrived.
 *
 * @param bundle - The loaded bundle
 * @param walk - The walk so far (entries and state committed)
 * @param inputOrdinal - This input's id namespace
 * @param slot - The response slot to speak
 * @param triggerContext - The provenance stamped on the entry (consumers find the line by it)
 * @param interpolations - The slot's placeholder values, when it has any
 * @param entryExtras - Extra fields the spoken entry carries into the RECORD (D86: the which-one ask attaches its `offeredAnswers`; every other caller passes nothing)
 * @returns The walk with the line spoken, committed, and its slot usage bumped
 */
export const speakLineOntoWalk = (
	bundle: EngineGameBundle,
	walk: ClauseWalk,
	inputOrdinal: number,
	slot: Parameters<typeof speakSlot>[2],
	triggerContext: string,
	interpolations?: Parameters<typeof speakSlot>[3],
	entryExtras?: Pick<RuntimeTranscriptEntry, 'offeredAnswers'>,
): ClauseWalk => {
	const spoken = speakSlot(bundle, walk.state, slot, interpolations);
	const stamped = stampNarrativeLines(
		bundle,
		walk.state,
		inputNamespaceCursor(inputOrdinal, walk.inputSequence, walk.state),
		[spoken.line],
		triggerContext,
	);
	const spokenEntries =
		entryExtras === undefined ? stamped.entries : stamped.entries.map((entry) => ({ ...entry, ...entryExtras }));
	const spokenState = appendTranscript(walk.state, spokenEntries);

	return {
		...walk,
		entries: [...walk.entries, ...spokenEntries],
		inputSequence: walk.inputSequence + spokenEntries.length,
		state: {
			...spokenState,
			session: {
				...spokenState.session,
				responseUsage: bumpResponseUsage(spokenState.session.responseUsage, [spoken.slot]),
			},
		},
	};
};
